import type { SVGProps } from 'react';

/**
 * Curated icon set. Defined inline so the bundle includes only what we use.
 * Decorative by default (`aria-hidden="true"`); pass `aria-label` and
 * `role="img"` if used standalone without an accessible text label.
 */
type IconComponent = (props: SVGProps<SVGSVGElement>) => JSX.Element;

const defaults: SVGProps<SVGSVGElement> = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
};

const Grid: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);
const Rocket: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M14 3l7 7-9 11-7-2-2-7L14 3z" />
    <circle cx="15" cy="9" r="1.5" />
  </svg>
);
const Code: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M8 6L2 12l6 6M16 6l6 6-6 6M14 4l-4 16" />
  </svg>
);
const Package: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M3 7l9-4 9 4v10l-9 4-9-4V7z" />
    <path d="M3 7l9 4 9-4M12 11v10" />
  </svg>
);
const Broadcast: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <circle cx="12" cy="12" r="2" />
    <path d="M8.5 8.5a5 5 0 000 7M15.5 8.5a5 5 0 010 7M5 5a10 10 0 000 14M19 5a10 10 0 010 14" />
  </svg>
);
const Flask: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M9 2v6L4 19a2 2 0 002 3h12a2 2 0 002-3l-5-11V2M9 2h6M7 14h10" />
  </svg>
);
const Card: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20M6 15h4" />
  </svg>
);
const Stack: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5M3 18l9 5 9-5" />
  </svg>
);
const Terminal: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M7 9l3 3-3 3M13 15h4" />
  </svg>
);
const Chart: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M3 3v18h18M7 14l4-4 4 4 5-7" />
  </svg>
);
const Pulse: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M3 12h4l2-7 4 14 2-7h6" />
  </svg>
);
const Help: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 3.5M12 17h0" />
  </svg>
);
const Search: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-5-5" />
  </svg>
);
const Bell: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M18 16v-5a6 6 0 10-12 0v5l-2 3h16l-2-3zM10 21a2 2 0 004 0" />
  </svg>
);
const Book: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M4 4h7a3 3 0 013 3v14a3 3 0 00-3-3H4V4zM20 4h-7a3 3 0 00-3 3v14a3 3 0 013-3h7V4z" />
  </svg>
);
const Vault: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="12" cy="12" r="3.5" />
    <path d="M12 8.5v-1.5M12 17v-1.5M8.5 12h-1.5M17 12h-1.5" />
  </svg>
);
const Tips: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M12 2v4M5 7l3 3M19 7l-3 3M3 14h18M5 14l1 7h12l1-7" />
    <circle cx="12" cy="11" r="1.5" />
  </svg>
);
const Capital: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M3 17l5-5 4 4 8-9M21 7v5h-5" />
  </svg>
);
const Identity: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="10" cy="11.5" r="2" />
    <path d="M6.5 17c0-1.5 1.5-3 3.5-3s3.5 1.5 3.5 3M15 9.5h4M15 13h3" />
  </svg>
);
const Shield: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M12 2l8 3v7c0 5-3.5 9-8 10-4.5-1-8-5-8-10V5l8-3z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const Ai: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M12 3l2 4 4 2-4 2-2 4-2-4-4-2 4-2 2-4zM19 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2zM5 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z" />
  </svg>
);
const Schema: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <ellipse cx="12" cy="5" rx="8" ry="2.5" />
    <path d="M4 5v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5V5M4 11v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-6" />
  </svg>
);
const Bundle: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M3 8l9-5 9 5-9 5-9-5zM3 14l9 5 9-5M3 18l9 5 9-5" />
  </svg>
);
const External: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M14 3h7v7M21 3l-9 9M19 14v5a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h5" />
  </svg>
);
const ChevronRight: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);
const Download: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M12 3v13m0 0l-5-5m5 5l5-5M4 21h16" />
  </svg>
);
const Plus: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const Bolt: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
  </svg>
);
const Filter: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M3 5h18l-7 9v6l-4-2v-4L3 5z" />
  </svg>
);
const Lock: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <rect x="5" y="11" width="14" height="10" rx="1.5" />
    <path d="M8 11V7a4 4 0 018 0v4" />
  </svg>
);
const Check: IconComponent = (p) => (
  <svg {...defaults} {...p} strokeWidth="2">
    <path d="M5 12l5 5L20 7" />
  </svg>
);
// Payment terminal (card machine / POS): device body, screen, keypad — distinct from the
// software `terminal` (console) icon.
const CardMachine: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <rect x="6" y="3" width="12" height="18" rx="2" />
    <rect x="9" y="6" width="6" height="4" rx="0.5" />
    <path d="M9.5 13h.01M12 13h.01M14.5 13h.01M9.5 16h.01M12 16h.01M14.5 16h.01" />
  </svg>
);
// Company / office building — Connect "Company Management".
const Building: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <rect x="4" y="3" width="16" height="18" rx="1" />
    <path d="M9 21v-4h6v4M8 7h.01M12 7h.01M16 7h.01M8 11h.01M12 11h.01M16 11h.01" />
  </svg>
);
// Storefront with awning — Connect "Customer Management".
const Storefront: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M4 9v11h16V9" />
    <path d="M2 9l2-5h16l2 5a3 3 0 01-6 0 3 3 0 01-6 0 3 3 0 01-6 0z" />
    <path d="M9 20v-6h6v6" />
  </svg>
);
// Hamburger — mobile nav trigger.
const Menu: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);
// Close — dismiss the mobile nav drawer / overlays.
const X: IconComponent = (p) => (
  <svg {...defaults} {...p}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const Icons = {
  grid: Grid,
  rocket: Rocket,
  code: Code,
  package: Package,
  broadcast: Broadcast,
  flask: Flask,
  card: Card,
  stack: Stack,
  terminal: Terminal,
  chart: Chart,
  pulse: Pulse,
  help: Help,
  search: Search,
  bell: Bell,
  book: Book,
  vault: Vault,
  tips: Tips,
  capital: Capital,
  identity: Identity,
  shield: Shield,
  ai: Ai,
  schema: Schema,
  bundle: Bundle,
  external: External,
  chevronRight: ChevronRight,
  download: Download,
  plus: Plus,
  bolt: Bolt,
  filter: Filter,
  lock: Lock,
  check: Check,
  cardMachine: CardMachine,
  building: Building,
  storefront: Storefront,
  menu: Menu,
  x: X,
} as const satisfies Record<string, IconComponent>;

export type IconName = keyof typeof Icons;

interface IconProps extends SVGProps<SVGSVGElement> {
  readonly name: IconName;
  readonly size?: number;
}

export function Icon({ name, size = 16, width, height, ...rest }: IconProps): JSX.Element {
  const Component = Icons[name];
  return <Component width={width ?? size} height={height ?? size} {...rest} />;
}
